import React, { useEffect, useMemo, useState } from "react";
import ForecastChart from "../components/ForecastChart";
import ForecastKPI from "../components/ForecastKPI";
import { fetchForecast, getForecastFromCache, inferColumnTypesFromRows } from "../utils/api";
import { useAppContext } from "../context/AppContext";
import "../styles/forecast.css";
const METRICS = [
  { key: "total_revenue", label: "Total Revenue" },
  { key: "total_expenses", label: "Total Expenses" },
  { key: "gross_profit", label: "Gross Profit" },
  { key: "net_income", label: "Net Income" },
];

const KEY_TO_LABEL = Object.fromEntries(METRICS.map(m => [m.key, m.label]));

const HORIZON_MONTHS = [3, 6, 12];
const HORIZON_QUARTERS = [2, 4, 8]; // ≈ 6, 12, 24 months but displayed as Qtrs

const isQuarterLabel = (lab = "") => /Q[1-4]/i.test(lab);

export default function ForecastPage() {
  const {
    user,
    token,
    hasUploaded,
    // demo/actual dataset coming from AppContext:
    csvData,
    setCsvData,
    columnTypes,
    setColumnTypes,
    companyName,
    setCompanyName,
    isDemo,
    // forecast cache in context
    forecastData,
    setForecastData
  } = useAppContext();


  // ---- UI state
  const [selectedKeys, setSelectedKeys] = useState(() => new Set(METRICS.map(m => m.key)));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [horizon, setHorizon] = useState(6); // months by default

  // ---- Data presence
  const hasData = useMemo(() => Array.isArray(csvData) && csvData.length > 0, [csvData]);

  // ---- Labels from current forecast
  const labels = useMemo(() => {
    const pts = forecastData?.points || [];
    return Array.from(new Set(pts.map(p => p.period))).sort();
  }, [forecastData]);

  const isQuarterly = useMemo(() => labels.some(isQuarterLabel), [labels]);

  // ---- Build preview for chart
  const preview = useMemo(() => {
    if (!forecastData) return null;

    const selectedLabels = Array.from(selectedKeys).map(k => KEY_TO_LABEL[k]).filter(Boolean);
    const pts = (forecastData.points || []).filter(p =>
      selectedLabels.length ? selectedLabels.includes(p.metric) : true
    );

    const allPeriods = Array.from(new Set(pts.map(p => p.period))).sort();

    let splitIndex = null;
    if (forecastData.split_period) {
      const idx = allPeriods.indexOf(forecastData.split_period);
      splitIndex = idx >= 0 ? Math.min(idx + 1, allPeriods.length - 1) : null;
    } else {
      const firstForecast = pts.filter(p => p.is_forecast).map(p => p.period).sort()[0];
      if (firstForecast) splitIndex = allPeriods.indexOf(firstForecast);
    }

    const metricNames =
      selectedLabels.length ? selectedLabels : Array.from(new Set(pts.map(p => p.metric)));

    const series = {};
    metricNames.forEach(m => {
      series[m] = allPeriods.map(per => {
        const match = pts.find(x => x.metric === m && x.period === per);
        if (!match) return null;
        const isPred =
          match.is_forecast === true ||
          (typeof match.predicted === "number" && (match.actual == null || Number.isNaN(match.actual))) ||
          (forecastData.split_period && String(per) > String(forecastData.split_period));
        return isPred
          ? (typeof match.predicted === "number" ? match.predicted : null)
          : (typeof match.actual === "number" ? match.actual : null);
      });
    });

    return {
      labels: allPeriods,
      keys: metricNames,
      series,
      splitIndex
    };
  }, [forecastData, selectedKeys]);

  // ---- Rehydrate cached forecast for signed-in users
  // Only rehydrate cached forecast in DEMO mode.
  // When a real CSV is uploaded (isDemo === false), never pull from cache.
  useEffect(() => {
    if (!isDemo) return;
    const cached = getForecastFromCache(user?.id);
    if (cached && !forecastData) setForecastData?.(cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, user?.id, forecastData, setForecastData]);
  // ---- Rehydrate last uploaded dataset on mount when csvData is empty
  // ---- Rehydrate last uploaded dataset on mount when csvData is empty
  useEffect(() => {
    if (Array.isArray(csvData) && csvData.length > 0) return;
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        setErr(null);
        // 1) Ask the server if a "last upload" exists
        const info = await fetchLastUpload(token); // from utils/api
        if (!info?.last_csv) return; // nothing to rehydrate; stay quiet

        // 2) Only then ask the server to reload it into app_state
        const res = await fetch(`${API_BASE}/account/last/reload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const ct = res.headers.get('content-type') || '';
        if (!res.ok || !ct.includes('json')) return; // silently ignore 404/410
        const j = await res.json();
        if (cancelled) return;

        if (Array.isArray(j?.data) && j.data.length) {
          setCsvData(j.data);
          if (j.column_types) setColumnTypes(j.column_types);
          if (j.company) setCompanyName(j.company);
        }
      } catch {
        // swallow; AppContext also tries to rehydrate and Account page provides manual load
      }
    })();
    return () => { cancelled = true; };
  }, [csvData, token, setCsvData, setColumnTypes, setCompanyName]);
  // ---- Helper: company name inference (mirrors Dashboard)
  const inferCompanyName = useMemo(() => {
    if (companyName && companyName.trim()) return companyName.trim();
    try {
      const first = csvData?.[0] || {};
      return first.Company || first.CompanyName || first.company || "Dataset";
    } catch {
      return "Dataset";
    }
  }, [companyName, csvData]);

  // ---- Run forecast (uses demo or uploaded rows)
  async function runForecast() {
    // do not return early; allow hydration to fetch rows if needed
    setErr(null);
    setLoading(true);
    // If we don't have rows in memory but the account has uploaded data,
    // rehydrate the last dataset so we always forecast on the intended CSV.
    if ((!Array.isArray(csvData) || csvData.length === 0) && hasUploaded && token) {
      try {
        const res = await fetch('/api/account/last/reload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const j = await res.json();
          if (Array.isArray(j?.data) && j.data.length) {
            setCsvData(j.data);
            if (j.column_types) setColumnTypes(j.column_types);
            if (j.company) setCompanyName(j.company);
          }
        }
      } catch { /* non-fatal: we'll still block fallback with Fix 2 */ }
    }

    try {
      // Build a non-empty mapping: use existing or infer from rows
      const effectiveColumnTypes =
        (columnTypes && Object.keys(columnTypes).length > 0)
          ? columnTypes
          : inferColumnTypesFromRows(Array.isArray(csvData) ? csvData : []);

      const payload = {
        horizon,
        userId: user?.id,
        columnTypes: effectiveColumnTypes,
        companyName: inferCompanyName,
        evaluation_mode: false
      };

      // ✅ Always include rows so backend forecasts the currently active dataset
      if (Array.isArray(csvData) && csvData.length > 0) {
        payload.rows = csvData;
      }

      const data = await fetchForecast(payload);

      setForecastData?.(data);
    } catch (e) {
      setErr(e?.message || "Forecast failed");
    } finally {
      setLoading(false);
    }
  }

  // ---- Auto-run forecast once demo (or upload) data is present
  // Manual mode: do not auto-run. User must click "Run Forecast".
  useEffect(() => {
    // no-op: manual trigger only
  }, [hasData]); 


  const toggleKey = (k) =>
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const selectAll = () => setSelectedKeys(new Set(METRICS.map(m => m.key)));
  const selectNone = () => setSelectedKeys(new Set());

  const horizonSet = isQuarterly ? HORIZON_QUARTERS : HORIZON_MONTHS;

  // ---- Empty-state while demo CSV loads (mirrors Dashboard)
  if (!hasData) {
    return (
      <div className="forecast-page">
        <header className="page-title">
          <h2>Forecast</h2>
          <p className="subtitle">
            {hasUploaded && !isDemo
              ? "Loading your last uploaded CSV…"
              : "Loading demo data… (or upload a CSV to see your own)"}
            {err ? ` — ${err}` : null}
          </p>
        </header>
      </div>
    );
  }


  return (
    <div className="forecast-page">
      <header className="page-title">
        <h2>Forecast</h2>
        <p className="subtitle">Interactive actuals vs forecast. Upload a CSV, then click “Run Forecast”.</p>
      </header>

      <section className="control-card">
        <div className="forecast-controls">
          <div className="controls-left">
            {/* Horizon pills */}
            <div className="control">
              <span className="control-label">Horizon</span>
              <div className="pill-row">
                {horizonSet.map((h) => {
                  const active = h === horizon || (isQuarterly && HORIZON_MONTHS[HORIZON_QUARTERS.indexOf(h)] === horizon);
                  return (
                    <button
                      key={h}
                      className={`toggle-pill ${active ? "active" : ""}`}
                      onClick={() => setHorizon(isQuarterly ? HORIZON_MONTHS[HORIZON_QUARTERS.indexOf(h)] : h)}
                    >
                      {isQuarterly ? `${h} Qtrs` : `${h} Mo`}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Metric toggles */}
            <div className="metric-toggles">
              <div className="toggles-title">Metrics</div>
              {METRICS.map((m) => {
                const active = selectedKeys.has(m.key);
                return (
                  <button
                    key={m.key}
                    className={`toggle-pill ${active ? "active" : ""}`}
                    onClick={() => toggleKey(m.key)}
                    aria-pressed={active}
                  >
                    {m.label}
                  </button>
                );
              })}
              <span className="toggle-actions">
                <button className="ghost-btn" onClick={selectAll}>All</button>
                <button className="ghost-btn" onClick={selectNone}>None</button>
              </span>
            </div>
          </div>

          <button className="primary-btn" onClick={runForecast} disabled={loading || !hasData}>
            {loading ? "Running…" : "Run Forecast"}
          </button>
        </div>
      </section>



      <section className="chart-wrap">
        {err && <p className="error">Forecast failed. {err}</p>}
        {preview ? (
          <ForecastChart preview={preview} />
        ) : (
          <div style={{ fontSize: 13, color: "var(--muted)" }}>
            {loading ? "Running forecast…" : "Run a forecast to populate the chart."}
          </div>
        )}
      </section>


      {forecastData && <ForecastKPI forecast={forecastData} />}

    </div>
  );
}
